'use client';

import { useState, useEffect } from 'react';
import { IMPERSONATE_FLAG_COOKIE, ADMIN_INFO_COOKIE, TARGET_USER_ID_COOKIE } from '@/lib/impersonate/cookie-names';
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
export function ImpersonateBanner() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetEmail, setTargetEmail] = useState<string | null>(null);
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  useEffect(() => {
    const flag = getCookie(IMPERSONATE_FLAG_COOKIE);
    if (flag === '1') {
      setIsImpersonating(true);
      setTargetEmail(getCookie(ADMIN_INFO_COOKIE));
      setTargetUserId(getCookie(TARGET_USER_ID_COOKIE));
    }
  }, []);
  if (!isImpersonating) return null;
  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      const res = await fetch('/impersonate/restore', {
        method: 'POST'
      });
      if (!res.ok && !res.redirected) {
        console.error('[ImpersonateBanner] Restore failed:', res.status);
        alert('Falha ao restaurar sessão. Faça logout manualmente.');
        return;
      }

      // The restore route has already written the admin session cookie to the
      // browser — it is shared across all tabs for this domain. We can safely
      // close this impersonate tab; the admin's existing backoffice tab will
      // pick up the restored cookie on its next navigation or refresh.
      if (window.opener && !window.opener.closed) {
        // Send the opener back to the impersonated user's backoffice page so
        // the admin lands directly on the user they were inspecting.
        const returnUrl = targetUserId ? `/backoffice/users/${targetUserId}` : '/backoffice';
        window.opener.location.href = returnUrl;
      }

      // Close this tab if it was opened programmatically (window.open).
      // Falls back to a same-tab redirect if the browser blocks window.close()
      // (e.g. the tab was not opened via script).
      if (window.close) {
        window.close();
      }

      // Fallback: if close() didn't work (tab not script-opened), navigate away.
      setTimeout(() => {
        window.location.href = res.redirected ? res.url : '/backoffice';
      }, 300);
    } catch (err) {
      console.error('[ImpersonateBanner] Restore error:', err);
      alert('Erro ao restaurar sessão.');
    } finally {
      setIsRestoring(false);
    }
  };

  // Collapsed state: small red circle in bottom-right corner
  if (isCollapsed) {
    return <button type="button" onClick={() => setIsCollapsed(false)} title="Modo Impersonate ativo — clique para expandir" style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      background: 'rgba(239, 68, 68, 0.95)',
      border: '2px solid rgba(255,255,255,0.3)',
      color: 'white',
      fontSize: '18px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 12px rgba(239,68,68,0.5)'
    }}>
        🔍
      </button>;
  }

  // Expanded state: full bottom bar
  return <div style={{
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: 'rgba(239, 68, 68, 0.95)',
    color: 'white',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '14px',
    fontFamily: 'system-ui, sans-serif',
    boxShadow: '0 -2px 12px rgba(0,0,0,0.3)',
    gap: '8px'
  }}>
      <span style={{
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}>
        🔍 <strong>Modo Impersonate</strong>
        {targetEmail ? ` — Visualizando como ${targetEmail}` : ''}
      </span>

      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0
    }}>
        <button type="button" onClick={handleRestore} disabled={isRestoring} style={{
        background: 'white',
        color: '#dc2626',
        border: 'none',
        borderRadius: '6px',
        padding: '6px 14px',
        fontWeight: 600,
        cursor: isRestoring ? 'not-allowed' : 'pointer',
        opacity: isRestoring ? 0.7 : 1,
        fontSize: '13px',
        whiteSpace: 'nowrap'
      }}>
          {isRestoring ? 'Restaurando...' : 'Voltar ao Backoffice'}
        </button>

        {/* Collapse button */}
        <button type="button" onClick={() => setIsCollapsed(true)} title="Recolher" style={{
        background: 'rgba(255,255,255,0.2)',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '6px 10px',
        cursor: 'pointer',
        fontSize: '13px',
        lineHeight: 1
      }}>
          ✕
        </button>
      </div>
    </div>;
}